// The VIZBI video library (fullstack /sitemaps/videos/showcases-lite.json, video names
// "Speaker[ & Speaker]: Title") matched to program talks, for every year's program page.
// A talk's video: same speaker, same year, and the same title, or (titles are often worded
// differently, e.g. "Proteins" vs "Visualizing Protein 3D Structures") a title sharing most of
// its key words. Name alone isn't enough: a speaker's tutorial would get their keynote's video.
import {ref} from "vue";

let loadStarted = false;
const entries = ref(null);

const MIN_TITLE_OVERLAP = 0.5; // share of the program title's key words found in the video title
// words that don't identify a talk ("visual…" is dropped too: "Visualizing X" = "X")
const FILLER_WORDS = new Set([
  "a", "an", "and", "by", "data", "for", "from", "how", "in", "into", "keynote", "of", "on",
  "or", "the", "to", "using", "via", "what", "why", "with"
]);

// soft hyphens (&shy;) vanish; other entities and tags act as spaces
function normalizeText(text) {
  return String(text || "")
    .replace(/&shy;|­/g, "")
    .replace(/&[a-z]+;|&#\d+;/gi, " ")
    .replace(/<[^>]+>/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function parseVideoEntry(videoId, video, albumYear) {
  const colonIndex = video.name.indexOf(":");
  if (colonIndex === -1) return null;
  const speakerName = video.name.slice(0, colonIndex).trim();
  const talkTitle = video.name.slice(colonIndex + 1).trim();
  const duration = Number(video.duration) || 0; // seconds (from Vimeo)
  return {id: videoId, speakerName, talkTitle, year: albumYear, duration};
}

// "Seán O'Donoghue & Christian Stolte" → each speaker, normalized
function getEntrySpeakers(entry) {
  return entry.speakerName.split(/\s*(?:&|,|\band\b)\s*/).map(normalizeText);
}

function getNameList(speakerNames) {
  const list = Array.isArray(speakerNames) ? speakerNames : [speakerNames];
  return list.map(normalizeText).filter(Boolean);
}

// key words, cut to 5 letters so plurals and endings match ("Proteins" ~ "Protein");
// "(sponsored by …)" and "[1/2]" are left out
function getTitleStems(title) {
  const text = normalizeText(String(title || "").replace(/\([^)]*\)|\[[^\]]*\]/g, " "));
  const words = text.split(" ").filter((word) => word && !FILLER_WORDS.has(word));
  return words.filter((word) => !word.startsWith("visual")).map((word) => word.slice(0, 5));
}

// 1 when the whole program title is inside the video title ("RNA Seq" ~ "RNA-Seq"), else the
// share of its key words that the video title has
function getTitleOverlap(programTitle, videoTitle) {
  const squashedProgram = normalizeText(programTitle).replace(/\s/g, "");
  if (!squashedProgram) return 0;
  if (normalizeText(videoTitle).replace(/\s/g, "").includes(squashedProgram)) return 1;
  const stems = getTitleStems(programTitle);
  if (!stems.length) return 0;
  const videoStems = new Set(getTitleStems(videoTitle));
  return stems.filter((stem) => videoStems.has(stem)).length / stems.length;
}

function getSpeakerEntries(names, year) {
  return entries.value.filter((entry) => {
    if (year && entry.year !== String(year)) return false;
    return getEntrySpeakers(entry).some((speaker) => names.includes(speaker));
  });
}

function getBestTitleMatch(candidates, talkTitle) {
  const title = normalizeText(talkTitle);
  const exact = candidates.find((entry) => normalizeText(entry.talkTitle) === title);
  if (exact) return exact;
  let best = null;
  let bestOverlap = 0;
  for (const entry of candidates) {
    const overlap = getTitleOverlap(talkTitle, entry.talkTitle);
    if (overlap < MIN_TITLE_OVERLAP || overlap <= bestOverlap) continue;
    [best, bestOverlap] = [entry, overlap];
  }
  return best;
}

function buildVideoIndex(showcases) {
  const result = [];
  for (const [albumName, album] of Object.entries(showcases)) {
    const yearMatch = albumName.match(/(\d{4})$/);
    if (!yearMatch) continue;
    const albumYear = yearMatch[1];
    for (const [videoId, video] of Object.entries(album.videos || {})) {
      const entry = parseVideoEntry(videoId, video, albumYear);
      if (entry) result.push(entry);
    }
  }
  return result;
}

// a failed load (a network error, or Safari's "Load failed" when the page is left mid-request)
// is caught, and the next use tries again: unhandled, it was a page error and left no videos
async function loadVideoIndex() {
  if (loadStarted) return;
  loadStarted = true;
  try {
    const response = await fetch("/sitemaps/videos/showcases-lite.json");
    if (!response.ok) throw new Error(`showcases-lite.json: ${response.status}`);
    entries.value = buildVideoIndex(await response.json());
  } catch (error) {
    loadStarted = false;
    console.warn(`Loading the video library: ${error.message}`);
  }
}

// Video icons only for items that are online: in programs with a Mode column (2024 on), an item
// whose Mode lacks "online" (e.g. "On-site only") gets no video, and no recording of its own.
// Earlier programs have no Mode (undefined), so this doesn't apply to them; nor to a video set
// in the sheet's Video column.
export function getCanHaveVideo(event) {
  if (event.video || event.Video) return true;
  if (event.mode === undefined) return true;
  return /online/i.test(event.mode || "");
}

export default function useShowcaseVideo() {
  if (!loadStarted) loadVideoIndex();
  // a talk's video: speakerNames is one name or the talk's speakers (a joint video
  // "A & B: title" matches either); the same title, else one sharing most key words
  function findVideoHash(speakerNames, talkTitle, year) {
    const names = getNameList(speakerNames);
    if (!entries.value || !names.length || !talkTitle) return null;
    const match = getBestTitleMatch(getSpeakerEntries(names, year), talkTitle);
    return match ? match.id : null;
  }
  // a speaker's recorded talk that year, by name only (e.g. November online speakers' April
  // talks). With several speakers, the first one (in program order) with a recording wins
  function findSpeakerVideoHash(speakerNames, year) {
    if (!entries.value || !year) return null;
    for (const name of getNameList(speakerNames)) {
      const match = getSpeakerEntries([name], year)[0];
      if (match) return match.id;
    }
    return null;
  }
  // a video's length in minutes, to the nearest 5 (e.g. 86 min → 85); null if unknown
  function getVideoMinutes(videoHash) {
    const entry = entries.value?.find((candidate) => candidate.id === videoHash);
    if (!entry?.duration) return null;
    return Math.max(5, Math.round(entry.duration / 300) * 5);
  }
  // which program event each library video records: the earliest event matching it (e.g. the
  // April talk, not a later summary or Q&A of it). events: [{ref, name, title, start}]
  function getVideoOwners(events, year) {
    const owners = new Map();
    const getTime = (event) => event.start?.toMillis?.() ?? Infinity;
    const sorted = [...events].sort((a, b) => getTime(a) - getTime(b));
    for (const event of sorted.filter(getCanHaveVideo)) {
      const hash = findVideoHash([event.name], event.title, year);
      if (hash && !owners.has(hash)) owners.set(hash, event.ref);
    }
    return owners;
  }
  return {findVideoHash, findSpeakerVideoHash, getVideoOwners, getVideoMinutes};
}
